/**
 * RETRY (prod fixes 2026-10-02, PLAN.md C5, UX-SPEC section 9): the browser's half of "retry a transient
 * failure once, server-side".
 *
 * When the first attempt fails with a transient class and the server wins its one retry, the edge does NOT
 * send the terminal failure. It sends one notification frame instead:
 *
 *     processStage=processing, processStageSubState=started, dataType=notification, data="AUTO_RETRY",
 *     autoRetry: { attempt: 2, processDomain?, downloadDomain? }
 *
 * and the job carries on, usually on ANOTHER edge. Everything here is pure (no services), so the card, the
 * file manager and the tests share one reading of that frame:
 *
 *  - `autoRetryStep` -- what one frame (or a status reply) means for a card: nothing, a repeat of a retry we
 *    already took in (ignored whole: it must not reset anything twice), or a new attempt (the card switches
 *    to it). A frame WITHOUT data="AUTO_RETRY" that carries a NEWER `autoRetry.attempt` also starts the
 *    attempt: a 1 s poll can miss the AUTO_RETRY frame when the dispatcher is quick, and the new edge's
 *    event counter starts wherever that JVM's clock put it, so its frames would otherwise fail the
 *    monotonic eventId guard and freeze the card.
 *  - `latchProgress` -- the card's progress latch. Monotonic within a stage exactly as before; it may go
 *    BACKWARDS only when the attempt changes (reset to 0, so no frozen "87%" from the dead attempt).
 *  - `triedTwiceNoteOf` -- the XR-2 line for a final failure, read from the structured `noteCodes`
 *    (C1), never from the human text.
 *
 * Copy (XR-1..XR-3) is UX-SPEC section 13 verbatim, the same strings as the C8 deck
 * (`constants/copy-deck.prod-fixes.ts`, T1). XR-1 and XR-3 show only with `featureFlags.autoRetryTransient`
 * exactly `true` (fail closed, as `utils/prod-fix-flags.ts`): with the flag off no new string can appear,
 * and an AUTO_RETRY frame that arrives anyway (a flag flip mid-job) reads as the long-standing
 * "Server Processing File". XR-2 is server-written (the note only exists after a real retry).
 */
import type { FileUpload } from '../models/file-upload';
import type { WebsocketResponse } from '../types/types';

export const AUTO_RETRY_COPY = {
    'XR-1': 'Retrying automatically — a hiccup on our side, not your file.',
    'XR-2': 'We already tried this twice.',
    'XR-3': 'Still working — we restarted it automatically after a hiccup.',
} as const;

/**
 * UX1-09: the next step on a failure after the retry. "We already tried this twice." must not be followed by,
 * or follow, "Please try again" (house rule: never tell the user to retry what we just retried), so the card
 * drops that tail from the last attempt's message (`failureMessageAfterRetry`) and ends XR-2 with this
 * instead: "Contact us" links to the existing contact page. Only on a card that carries the server's
 * AUTO_RETRY_TRIED_TWICE note, so it inherits XR-2's gate (the server flag): flag off, it cannot appear.
 */
export const AUTO_RETRY_NEXT_STEP = { link: 'Contact us', href: '/contact-us', tail: ' and we\'ll take a look.' } as const;

/** The frame's `data` marker (C5). No new enum value, so an older browser just shows a notification. */
export const AUTO_RETRY_DATA = 'AUTO_RETRY';
/** The structured note the final failed attempt carries (C1 table, writer T11). */
export const AUTO_RETRY_TRIED_TWICE_CODE = 'AUTO_RETRY_TRIED_TWICE';
/** The status row's text for an AUTO_RETRY frame with the flag off: an existing string, never the raw marker. */
export const AUTO_RETRY_FLAG_OFF_TEXT = 'Server Processing File';

/** `autoRetry` as the edge frame or the status reply carries it. Every piece may be absent. */
export interface AutoRetryWire {
    attempt?: number;
    state?: string;
    processDomain?: string;
    downloadDomain?: string;
}

export type { CardAutoRetry } from '../models/file-upload';

/** Server-side states after which the retry is over (C5 `AutoRetryState.state`). */
const SETTLED_STATES = new Set(['DONE', 'FAILED']);

/** Is the edge's `featureFlags.autoRetryTransient` on? Strict `=== true`, as every prod-fix flag. */
export function autoRetryCopyOn(flags: unknown): boolean {
    return (flags as Record<string, unknown> | null | undefined)?.autoRetryTransient === true;
}

/**
 * An edge domain as the servers send it, or null. Both forms are real: the card and professorx's dispatcher
 * frame use `https://host` (`URLUtils.convertURLToHttps`), ironman's status block the bare `EdgeModel.domain`.
 * Anything with a path, query, credentials or odd characters is dropped: it ends up in URLs the browser
 * sends its bearer to. Returned trimmed, without a trailing slash.
 */
function edgeDomainOf(v: unknown): string | null {
    if (typeof v !== 'string') return null;
    const s = v.trim().replace(/\/+$/, '');
    return /^(https?:\/\/)?[A-Za-z0-9.-]+(:\d{1,5})?$/i.test(s) && /[A-Za-z0-9]/.test(s) ? s : null;
}

/** The host[:port] of an edge domain in either form, lower-cased, for comparing (never for URLs). */
export function edgeHostOf(v: unknown): string {
    return typeof v === 'string' ? v.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase() : '';
}

/** [domain] written in the form the card already uses (`https://host` or bare), so a switch never mixes forms. */
function inCardForm(domain: string, cardDomain: unknown): string {
    const host = edgeHostOf(domain);
    return typeof cardDomain === 'string' && /^https?:\/\//i.test(cardDomain.trim()) ? `https://${host}` : host;
}

function parseResult(result: unknown): any {
    if (typeof result !== 'string' || !result) return result && typeof result === 'object' ? result : null;
    try { return JSON.parse(result); } catch { return null; }
}

/**
 * The `autoRetry` block of a frame or status reply, normalised, or null. Read from the top level (C5) or,
 * defensively, from the frame's JSON `result`. A domain that is not a bare host name is dropped: it ends
 * up in URLs the browser sends its bearer to.
 */
export function autoRetryWireOf(source: unknown): AutoRetryWire | null {
    if (!source || typeof source !== 'object') return null;
    const s = source as any;
    const raw = (s.autoRetry && typeof s.autoRetry === 'object' ? s.autoRetry : null)
        ?? parseResult(s.result)?.autoRetry
        ?? null;
    if (!raw || typeof raw !== 'object') return null;
    const attempt = Number(raw.attempt);
    const out: AutoRetryWire = {};
    if (Number.isInteger(attempt) && attempt >= 2) out.attempt = attempt;
    if (typeof raw.state === 'string' && raw.state) out.state = raw.state;
    const pd = edgeDomainOf(raw.processDomain);
    const dd = edgeDomainOf(raw.downloadDomain);
    if (pd) out.processDomain = pd;
    if (dd) out.downloadDomain = dd;
    return Object.keys(out).length ? out : null;
}

/** The C5 frame: data="AUTO_RETRY" on a non-terminal frame. Anything else is an ordinary frame. */
export function isAutoRetryFrame(ws: Partial<WebsocketResponse> | null | undefined): boolean {
    return !!ws && ws.data === AUTO_RETRY_DATA
        && ws.processStageSubState !== 'failed'
        && ws.processStage !== 'completed';
}

/** The attempt the card is on: 1 until a retry was taken in. */
export function attemptOf(fileUpload: Pick<FileUpload, 'autoRetry'> | null | undefined): number {
    const a = fileUpload?.autoRetry?.attempt;
    return typeof a === 'number' && a >= 2 ? a : 1;
}

export type AutoRetryStepKind = 'none' | 'repeat' | 'started';

export interface AutoRetryStep {
    /** `started`: a new attempt; `repeat`: an AUTO_RETRY frame for an attempt already taken in; `none`: neither. */
    kind: AutoRetryStepKind;
    /** The attempt the card should be on after this frame. */
    attempt: number;
    /** New edge domains to switch to (only those that differ from the card's). */
    processDomain?: string;
    downloadDomain?: string;
}

function metadataOf(fileUpload: any): any {
    return fileUpload?.endpointData?.processorMetadataList?.[0] ?? null;
}

/**
 * What one frame means for one card. Pure: reads the card, changes nothing ([applyAutoRetryStep] does).
 * A domain switch is offered only together with an attempt at least as new as the card's, so a late frame
 * from the dead edge can never move the card back.
 */
export function autoRetryStep(fileUpload: FileUpload | null | undefined, frame: unknown): AutoRetryStep {
    const current = attemptOf(fileUpload);
    const wire = autoRetryWireOf(frame);
    const marker = isAutoRetryFrame(frame as any);
    if (!marker && !wire) return { kind: 'none', attempt: current };
    // The marker without an attempt number is attempt 2: there is only ever one retry (autoRetryMaxAttempts <= 1).
    const announced = wire?.attempt ?? (marker ? 2 : current);
    const settled = !!wire?.state && SETTLED_STATES.has(wire.state);
    let kind: AutoRetryStepKind = 'none';
    if (announced > current && (marker || !settled)) kind = 'started';
    else if (marker) kind = 'repeat';
    const step: AutoRetryStep = { kind, attempt: Math.max(current, announced) };
    // A domain is taken only from a block that names its attempt: an unnumbered one could be the dead attempt's.
    if (wire?.attempt !== undefined && wire.attempt >= current) {
        const md = metadataOf(fileUpload);
        // Compared by host: ironman says `edge7.xconvert.com`, professorx `https://edge7.xconvert.com` -- the same edge.
        if (wire.processDomain && edgeHostOf(wire.processDomain) !== edgeHostOf(md?.processDomain)) {
            step.processDomain = inCardForm(wire.processDomain, md?.processDomain);
        }
        if (wire.downloadDomain && edgeHostOf(wire.downloadDomain) !== edgeHostOf(md?.downloadDomain)) {
            step.downloadDomain = inCardForm(wire.downloadDomain, md?.downloadDomain);
        }
    }
    return step;
}

/**
 * Apply [step] to the card: remember the attempt and switch its edge domains. Returns true when a domain
 * changed (the caller re-tracks the card against the new edge). Does not touch `ws` or `result`: the
 * file manager's ordinary frame handling does that.
 */
export function applyAutoRetryStep(fileUpload: FileUpload, step: AutoRetryStep, now: Date = new Date()): boolean {
    const md = metadataOf(fileUpload);
    if (step.kind === 'started') {
        const from = edgeDomainOf(md?.processDomain);
        fileUpload.autoRetry = { attempt: step.attempt, since: now.toISOString(), ...(from ? { fromProcessDomain: from } : {}) };
        if (fileUpload.id) pendingAnnouncements.set(String(fileUpload.id), step.attempt);
    }
    let moved = false;
    if (md && step.processDomain) { md.processDomain = step.processDomain; moved = true; }
    if (md && step.downloadDomain) { md.downloadDomain = step.downloadDomain; moved = true; }
    return moved;
}

/**
 * Should the pod tracker's per-edge fallback leave this card to the pod? Yes while it is retrying and the
 * card still names the failed attempt's edge (the new one is not known yet): that edge may be the reason
 * for the retry, and one dead host in the fallback's forkJoin fails the poll for every card.
 */
export function awaitingNewEdge(fileUpload: FileUpload | null | undefined): boolean {
    const from = fileUpload?.autoRetry?.fromProcessDomain;
    if (!from || !isAutoRetrying(fileUpload)) return false;
    return edgeHostOf(metadataOf(fileUpload)?.processDomain) === edgeHostOf(from);
}

/**
 * XR-1 is announced ONCE per retry, and only for a retry this page saw start (a card restored by a reload
 * that was already retrying is not announced again). Kept here, not on the card instance: the list re-sorts
 * a card that changes, which remounts it, and the new instance must still say it -- once.
 */
const pendingAnnouncements = new Map<string, number>();

/** Is XR-1 still owed for the retry [attempt] of card [id] (it started while this page was open)? */
export function retryAnnouncementPending(id: string | null | undefined, attempt: number): boolean {
    const key = String(id ?? '');
    return !!key && pendingAnnouncements.get(key) === attempt;
}

/**
 * Settle the debt: true exactly once per retry. Called when the text actually goes into a mounted region,
 * so a card that unmounts between scheduling and saying it leaves the debt for its next instance.
 */
export function markRetryAnnounced(id: string | null | undefined, attempt: number): boolean {
    if (!retryAnnouncementPending(id, attempt)) return false;
    pendingAnnouncements.delete(String(id));
    return true;
}

/** Is this card between "we are retrying" and its final outcome? */
export function isAutoRetrying(fileUpload: FileUpload | null | undefined): boolean {
    if (!fileUpload || attemptOf(fileUpload) < 2) return false;
    const ws = fileUpload.ws;
    if (ws && (ws.processStageSubState === 'failed' || ws.processStage === 'completed')) return false;
    return !(fileUpload.result && ((fileUpload.result as any).output || fileUpload.result.error === true));
}

/** The status-row text for an AUTO_RETRY frame (XR-1, or the existing generic line with the flag off). */
export function autoRetryStatusText(copyOn: boolean): string {
    return copyOn ? AUTO_RETRY_COPY['XR-1'] : AUTO_RETRY_FLAG_OFF_TEXT;
}

/**
 * Check Status while the retry runs: XR-3, or null to keep today's toast. Only for a verdict that is not
 * terminal (`unknown`); a reply that says the job finished keeps its own toast.
 */
export function checkStatusRetryToast(
    fileUpload: FileUpload | null | undefined,
    verdict: { state?: string; data?: unknown } | null | undefined,
    copyOn: boolean,
): string | null {
    if (!copyOn || !verdict || verdict.state === 'success' || verdict.state === 'error') return null;
    const wire = autoRetryWireOf(verdict.data);
    const serverSaysRetrying = !!wire && (wire.attempt ?? 0) >= 2 && !(wire.state && SETTLED_STATES.has(wire.state));
    return serverSaysRetrying || isAutoRetrying(fileUpload) ? AUTO_RETRY_COPY['XR-3'] : null;
}

/** The card's progress latch (see `file-list-item.component.tsx` render). */
export interface ProgressLatch {
    pct: number;
    stage: string;
    /** The attempt the value belongs to; absent on a latch from before this field (attempt 1). */
    attempt?: number;
}

/**
 * Next latch value. [value] is the frame's decoded progress, or NaN/null when the frame carries none.
 * Monotonic within one stage of one attempt (multi-pass encodes never run the bar backwards); a new stage
 * starts from its own first value, as before. A NEW ATTEMPT is the only thing that moves it back: to 0 in
 * the frame's stage, then the frame's own value if it has one.
 */
export function latchProgress(
    prev: ProgressLatch | null,
    stage: string | null | undefined,
    value: number | null | undefined,
    attempt: number,
): ProgressLatch | null {
    let latch = prev;
    if (latch && attempt > (latch.attempt ?? 1)) {
        latch = { pct: 0, stage: stage || latch.stage, attempt };
    }
    if (value === null || value === undefined || Number.isNaN(value) || !stage) return latch;
    if (!latch || latch.stage !== stage) return { pct: value, stage, attempt };
    return value > latch.pct ? { ...latch, pct: value } : latch;
}

export interface TriedTwiceNote {
    text: string;
    code: string;
}

/**
 * The XR-2 line a final failure shows under its message, from the structured note (C1). The edge's own
 * text wins (it is the same deck string); XR-2 is the fallback. Null when the result carries no such note.
 */
export function triedTwiceNoteOf(result: unknown): TriedTwiceNote | null {
    const codes = (result as any)?.noteCodes;
    if (!Array.isArray(codes)) return null;
    const nc = codes.find(c => c && c.code === AUTO_RETRY_TRIED_TWICE_CODE);
    if (!nc) return null;
    const text = typeof nc.text === 'string' && nc.text.trim() ? nc.text : AUTO_RETRY_COPY['XR-2'];
    return { text, code: AUTO_RETRY_TRIED_TWICE_CODE };
}

/** "later", "in a few minutes", ... -- the only words allowed after "try again" in a tail we drop. */
const TRY_AGAIN_QUALIFIER = String.raw`(?:\s+(?:later|soon|shortly|in\s+a\s+(?:few\s+)?(?:minutes?|moments?|while|bit)))?`;
/** A last sentence "Please try again[ later]." (also "Try again!", "Please try again !!"). */
const TRY_AGAIN_SENTENCE = new RegExp(String.raw`(^|[.!?])\s*(?:please\s+)?try\s+again${TRY_AGAIN_QUALIFIER}\s*[.!?]*\s*$`, 'i');
/** A last clause ", please try again." / " — please try again later." */
const TRY_AGAIN_CLAUSE = new RegExp(String.raw`\s*(?:[,;]|\s[—–])\s*(?:please\s+)?try\s+again${TRY_AGAIN_QUALIFIER}\s*[.!?]*\s*$`, 'i');

/**
 * UX1-09: the last attempt's message as a card that failed AFTER the retry shows it. The decision is the
 * structured note ([triedTwice], C1), never the text; this only trims, for display, a closing "Please try
 * again" sentence or clause (every real server message ends that way, e.g. "We couldn't get your file to the
 * converter. Please try again."), since XR-2 and its next step follow it. "Try again" inside a sentence that
 * says something else ("Please wait a few minutes and try again.", "Try again with a smaller file") is kept.
 * Without the note, the text is returned untouched. A message that was only the tail gives '' (the card
 * then shows its generic "Conversion failed.").
 */
export function failureMessageAfterRetry(message: unknown, triedTwice: TriedTwiceNote | null): string {
    const text = typeof message === 'string' ? message : '';
    if (!triedTwice || !text) return text;
    const sentence = text.replace(TRY_AGAIN_SENTENCE, '$1');
    const trimmed = sentence !== text ? sentence : text.replace(TRY_AGAIN_CLAUSE, '.');
    return trimmed === text ? text : trimmed.trim().replace(/^[.!?]+$/, '');
}
